import { createTheme } from '@mui/material/styles'

/**
 * Singa Business, dark.
 *
 * Two sources, and they disagree, so it is worth writing down which won where.
 *
 * `temp/design-sample/.../colors_and_type.css` is the Singa Business design
 * system. It documents the **light** variant and calls itself Figma-aligned and
 * authoritative, and its README says "where the two disagree on visuals, the
 * Figma wins". Everything not to do with surface colour comes from there: the
 * brand green, the 6px radius, Inter, the type scale, dark ink on green fills.
 *
 * The dark surfaces come from the product screenshot in the same folder,
 * sampled pixel by pixel, because the CSS has nothing to say about them. Note
 * the screenshot's green is #5bda6e rather than the documented #17E87A -- a
 * genuinely different hue, not a rendering artefact. We follow the design
 * system, so the accent here is the documented one.
 *
 * Change values here, never in a component's `sx`.
 */

/** Singa Green, anchor 500 = oklch(0.78 0.23 150). Primary fills take dark ink. */
const GREEN = '#17E87A'
const GREEN_HOVER = '#12b961' // green-70, the design system's active indicator
const INK_ON_GREEN = '#1a1a1a' // grey-90; the green is light, so fills force dark text

/**
 * Dark surfaces, sampled from the screenshot. The panel is *lighter* than the
 * sidebar and cards are lighter again -- depth comes from lightness, not from
 * borders, which is why cards here have none.
 */
const SIDEBAR = '#0a0a0a'
const APP_BG = '#141414'
const CARD = '#181819'
/** Selected sidebar row, and the raised half of a segmented control. */
export const SURFACE_SELECTED = '#1d1d1d'
export const SURFACE_RAISED = '#2f2f33'

const TEXT_PRIMARY = '#fafafa' // 17.7:1 on the panel
const TEXT_SECONDARY = '#8a8a90' // 5.4:1 -- passes AA for body text
/**
 * Genuinely disabled controls only, at 3.5:1. WCAG exempts disabled elements,
 * but that makes it the wrong colour for content: "Disabled", "(deleted)" and
 * similar words are things to read, so they use text.secondary instead.
 */
const TEXT_DISABLED = '#6b6b6f'

/**
 * The design system's red-60 is #d33f2c, chosen against white. On these
 * surfaces it manages 3.96:1 -- under the 4.5:1 that body text needs, and it is
 * used as text far more often than as a fill (rejection reasons, failed job
 * chips, the GPU error banner). This is the same hue and saturation lightened
 * for a dark ground: 5.3:1 on the panel, and dark ink on it reaches 5.0:1, so
 * filled destructive buttons follow the same "light accent, dark ink" rule the
 * green does.
 */
const RED = '#dc6556'

export const theme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: APP_BG, paper: CARD },
    primary: { main: GREEN, dark: GREEN_HOVER, contrastText: INK_ON_GREEN },
    // The design system uses brand green for informational emphasis rather than
    // a second hue, so there is deliberately no competing accent.
    secondary: { main: GREEN, contrastText: INK_ON_GREEN },
    success: { main: GREEN, contrastText: INK_ON_GREEN },
    error: { main: RED, contrastText: INK_ON_GREEN },
    warning: { main: '#f59e0b' },
    // The design system says info is brand green rather than a blue, and sets
    // the "ongoing" indicator to green-500. green-80 (#0e8b49) is what the light
    // theme uses, but it only reaches 4.2:1 here and is the colour every
    // in-progress job chip is drawn in, so the brand green does the job at
    // 11.3:1.
    info: { main: GREEN, contrastText: INK_ON_GREEN },
    text: { primary: TEXT_PRIMARY, secondary: TEXT_SECONDARY, disabled: TEXT_DISABLED },
    divider: 'rgba(255, 255, 255, 0.08)',
    action: { hover: 'rgba(255, 255, 255, 0.04)', selected: 'rgba(255, 255, 255, 0.06)' },
  },

  // radius-md, used for buttons, inputs, cards and modals alike. Was 12, which
  // read softer than anything Singa ships.
  shape: { borderRadius: 6 },

  typography: {
    // Inter, self-hosted through @fontsource. The design system loads it from
    // Google Fonts; our CSP is font-src 'self' data: and there are no
    // third-party origins in this app, so pulling it from a CDN would both
    // break and be a regression.
    fontFamily: '"Inter", ui-sans-serif, system-ui, "Helvetica Neue", Arial, sans-serif',
    // 14px body, per the design system's --fs-sm default.
    body1: { fontSize: '0.875rem', lineHeight: 1.43 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.4 }, // .meta
    caption: { fontSize: '0.75rem', lineHeight: 1.4 }, // .meta-sm
    // Page titles: 24px bold with the design system's slight positive tracking.
    h5: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '0.0094em', lineHeight: 1.2 },
    h6: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.25 },
    button: { fontSize: '0.875rem', fontWeight: 500 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: APP_BG, WebkitFontSmoothing: 'antialiased' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // Singa writes buttons in Title Case; MUI shouts them by default.
        root: { textTransform: 'none', padding: '8px 16px' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // MUI paints a lightening gradient over dark paper, which fights a
        // palette whose surfaces are already deliberately ordered by lightness.
        root: { backgroundImage: 'none' },
      },
    },
    MuiDrawer: {
      styleOverrides: { paper: { backgroundColor: SIDEBAR, backgroundImage: 'none' } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 500 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: SURFACE_RAISED,
          fontSize: '0.75rem',
          // Without a cap a long tooltip becomes a slab the width of the panel.
          maxWidth: 320,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { borderColor: 'rgba(255, 255, 255, 0.08)' } },
    },
  },
})
